import { match } from 'ts-pattern';
import type { AIMessage } from '~/common/vm/protocol';
import { AIActionCard } from './ai-messages/AIActionCard';
import { AIAssertionCard } from './ai-messages/AIAssertionCard';
import { AIChainOfThoughtText } from './ai-messages/AIChainOfThoughtText';
import { AIComponentCallCard } from './ai-messages/AIComponentCallCard';
import { AICompletionCard } from './ai-messages/AICompletionCard';
import { AIDecisionCard } from './ai-messages/AIDecisionCard';
import { AIDefineVariableCard } from './ai-messages/AIDefineVariableCard';
import { AIDialogCard } from './ai-messages/AIDialogCard';
import { AIExtractionResultCard, AIExtractionStartCard } from './ai-messages/AIExtractionCard';
import { AIGenerationStatusCard } from './ai-messages/AIGenerationStatusCard';
import { AIKnowledgeRetrievalCard } from './ai-messages/AIKnowledgeRetrievalCard';
import { AIKnowledgeSuggestionCard } from './ai-messages/AIKnowledgeSuggestionCard';
import { AIScreenshotAnalysisCard } from './ai-messages/AIScreenshotAnalysisCard';
import { AutohealStepStatusCard } from './ai-messages/AutohealStepStatusCard';
import { AIStatusCard } from './ai-messages/AIStatusCard';
import { AITipCard } from './ai-messages/AITipCard';
import {
  AIConditionalBlockResultCard,
  AIVisualAssertionResultCard,
  AIVisualAssertionStartCard,
} from './ai-messages/AIVisualAssertionCard';

export function AIMessageRenderer({
  message,
  isLastMessage,
  isPreviewing,
  autoExpandScreenshotAnalysis = false,
  onSeekToVideoSeconds,
}: {
  message: AIMessage;
  isLastMessage: boolean;
  isPreviewing: boolean;
  autoExpandScreenshotAnalysis?: boolean;
  onSeekToVideoSeconds?: (seconds: number) => void;
}) {
  const content = match(message)
    .with({ type: 'chain-of-thought' }, (message) => <AIChainOfThoughtText message={message} />)
    .with({ type: 'knowledge-retrieval' }, (message) => <AIKnowledgeRetrievalCard message={message} />)
    .with({ type: 'knowledge-suggestion' }, (message) =>
      match(message.actionId)
        .with('save-knowledge', () => <AIKnowledgeSuggestionCard message={message} />)
        .exhaustive(),
    )
    .with({ type: 'observation' }, () => null) // for now, we don't show observation messages
    .with({ type: 'action' }, (message) => <AIActionCard message={message} />)
    .with({ type: 'completion' }, (message) => <AICompletionCard message={message} />)
    .with({ type: 'status' }, (message) => <AIStatusCard message={message} />)
    .with({ type: 'ai-generation-status' }, (message) => <AIGenerationStatusCard message={message} />)
    .with({ type: 'autoheal-step-status' }, (message) => <AutohealStepStatusCard message={message} />)
    .with({ type: 'component-call' }, (message) => <AIComponentCallCard message={message} />)
    .with({ type: 'tip' }, (message) => <AITipCard message={message} />)
    .with({ type: 'define-variable' }, (message) => <AIDefineVariableCard message={message} />)
    .with({ type: 'decision' }, (message) => <AIDecisionCard message={message} />)
    .with({ type: 'assertion' }, (message) => <AIAssertionCard message={message} />)
    .with({ type: 'dialog' }, (message) => <AIDialogCard message={message} />)
    .with({ type: 'ai-extraction-start' }, (message) => <AIExtractionStartCard message={message} />)
    .with({ type: 'ai-extraction-result' }, (message) => <AIExtractionResultCard message={message} />)
    .with({ type: 'ai-assertion-start' }, (message) => <AIVisualAssertionStartCard message={message} />)
    .with({ type: 'ai-assertion-result' }, (message) => <AIVisualAssertionResultCard message={message} />)
    .with({ type: 'conditional-evaluation-result' }, (message) => <AIConditionalBlockResultCard message={message} />)
    .with({ type: 'screenshot-analysis' }, (message) => (
      <AIScreenshotAnalysisCard
        message={message}
        isLastMessage={isLastMessage}
        isPreviewing={isPreviewing}
        autoExpand={autoExpandScreenshotAnalysis}
      />
    ))
    .exhaustive();

  const isClickable = message.videoTimestampSeconds !== undefined && !!onSeekToVideoSeconds;
  return (
    <div
      onClick={
        isClickable ?
          (e) => {
            // Ignore clicks originating from interactive elements
            if (e.target instanceof Element) {
              const interactive = e.target.closest(
                'button,a,input,textarea,select,[role="button"],[contenteditable="true"]',
              );
              if (interactive && e.currentTarget.contains(interactive)) {
                return;
              }
            }
            if (onSeekToVideoSeconds && message.videoTimestampSeconds !== undefined) {
              onSeekToVideoSeconds(message.videoTimestampSeconds);
            }
          }
        : undefined
      }
      className={isClickable ? 'cursor-pointer' : undefined}
      title={isClickable ? 'Jump to this moment in the video' : undefined}
    >
      {content}
    </div>
  );
}
